import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { MapPin, Target, MessageSquare, Users, HandHeart, Check, ChevronRight, X, Sparkles } from "lucide-react";

// First-run getting-started checklist for admins. Dismissible; auto-hides once all steps are done.
export default function GettingStarted({ orgId, countryCount = 0, goalCents = 0, dispatchCount = 0, partnerCount = 0, donateUrl }) {
  const navigate = useNavigate();
  const storageKey = `mc_gs_hidden_${orgId || "org"}`;
  const [hidden, setHidden] = useState(() => localStorage.getItem(storageKey) === "1");

  const steps = [
    { key: "trip", icon: MapPin, label: "Map your mission trip", desc: "Add the countries & dates you'll visit.", done: countryCount > 0, to: "/build-trip", cta: "Build trip" },
    { key: "goal", icon: Target, label: "Set your funding goal", desc: "Set a goal & plan your budget before logging expenses.", done: goalCents > 0, to: "/app/budget?tab=budget", cta: "Set goal" },
    { key: "dispatch", icon: MessageSquare, label: "Post your first dispatch", desc: "Share a photo or update from the field.", done: dispatchCount > 0, to: "/app/feed", cta: "Post" },
    { key: "partners", icon: Users, label: "Invite your partners", desc: "Bring supporters along for the journey.", done: partnerCount > 0, to: "/app/partners", cta: "Invite" },
    { key: "donate", icon: HandHeart, label: "Add a donation link", desc: "Let partners give with one tap.", done: !!donateUrl, to: "/app/trip-settings", cta: "Add link" },
  ];

  const doneCount = steps.filter((s) => s.done).length;
  const pct = Math.round((doneCount / steps.length) * 100);
  const allDone = doneCount === steps.length;

  if (hidden || allDone) return null;

  const dismiss = () => { localStorage.setItem(storageKey, "1"); setHidden(true); };

  return (
    <motion.div
      data-testid="getting-started-card"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="rounded-3xl bg-white p-6 shadow-[0_8px_32px_rgba(44,44,62,0.08)] border border-[var(--tenant-accent)]/25"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="h-9 w-9 rounded-xl bg-[var(--tenant-accent)]/15 flex items-center justify-center">
            <Sparkles className="h-5 w-5 accent-text" />
          </div>
          <div>
            <h2 className="font-heading font-bold text-lg text-[var(--brand-navy)] leading-tight">Let's get you set up</h2>
            <p className="text-xs text-muted-foreground">{doneCount} of {steps.length} done · finish to launch your space</p>
          </div>
        </div>
        <button data-testid="getting-started-dismiss" onClick={dismiss} className="text-muted-foreground hover:text-[var(--brand-navy)] shrink-0" aria-label="Dismiss">
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="mt-4 h-2 rounded-full bg-muted overflow-hidden">
        <motion.div className="h-full accent-bg rounded-full" initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 0.7, ease: "easeOut" }} />
      </div>

      <div className="mt-4 space-y-2">
        <AnimatePresence initial={false}>
          {steps.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.button
                key={s.key}
                data-testid={`gs-step-${s.key}`}
                onClick={() => !s.done && navigate(s.to)}
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.05 }}
                className={`w-full flex items-center gap-3 rounded-2xl p-3 text-left transition-colors ${
                  s.done ? "bg-[var(--tenant-accent)]/8 cursor-default" : "bg-muted/40 hover:bg-muted/70"
                }`}
              >
                <div className={`h-9 w-9 rounded-xl flex items-center justify-center shrink-0 ${s.done ? "accent-bg text-white" : "bg-white text-[var(--brand-navy)]"}`}>
                  {s.done ? <Check className="h-5 w-5" /> : <Icon className="h-5 w-5" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className={`font-semibold text-sm ${s.done ? "text-muted-foreground line-through" : "text-[#2C2C3E]"}`}>{s.label}</div>
                  {!s.done && <div className="text-xs text-muted-foreground truncate">{s.desc}</div>}
                </div>
                {!s.done && (
                  <span className="flex items-center gap-0.5 text-xs font-semibold accent-text shrink-0">{s.cta} <ChevronRight className="h-3.5 w-3.5" /></span>
                )}
              </motion.button>
            );
          })}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}
